import React, { useEffect, useState } from 'react';
import type ReactECharts from 'echarts-for-react';
import './BrandAxisAvatars.scss';
import BrandIcon from '../../brand-icon/BrandIcon';

export interface BrandAxisAvatarItem {
  /** The x-axis category value the badge is centred on. */
  name: string;
  color: string;
}

interface BrandAxisAvatarsProps {
  chartRef: React.RefObject<ReactECharts>;
  items: BrandAxisAvatarItem[];
  size?: 'xs' | 'sm' | 'md';
  /** Distance below the y=0 baseline, px. */
  offsetY?: number;
}

/**
 * BrandIcon badges over a category x-axis — one per item, centred on the category's x just below the
 * baseline (convertToPixel). Rich-text circles inside axis labels don't centre reliably, hence HTML
 * overlays: the chart wrapper must be `position: relative`, the axis labels need a `margin` (~42px for
 * `md`) to leave room above the names, and the badges are NOT in the canvas (PDF snapshots of the chart
 * omit them). Remount it together with the chart (same keyed Fragment) whenever the chart is remounted,
 * e.g. on theme change — it observes the chart DOM it found on mount.
 */
export const BrandAxisAvatars: React.FC<BrandAxisAvatarsProps> = ({
  chartRef,
  items,
  size = 'md',
  offsetY = 8,
}) => {
  const [width, setWidth] = useState(0);
  const [positions, setPositions] = useState<Array<BrandAxisAvatarItem & { left: number; top: number }>>(
    []
  );

  // Track the chart width so positions recompute on resize (the first callback fires on observe).
  useEffect(() => {
    const container = chartRef.current?.getEchartsInstance().getDom();
    if (!container) return undefined;
    const observer = new ResizeObserver(() => setWidth(container.clientWidth));
    observer.observe(container);
    return () => observer.disconnect();
  }, [chartRef]);

  useEffect(() => {
    if (width <= 0 || items.length === 0) {
      setPositions([]);
      return undefined;
    }
    // Defer so ECharts has laid out its coordinate system (and finished its own resize) first.
    const timer = setTimeout(() => {
      const instance = chartRef.current?.getEchartsInstance();
      if (!instance || instance.isDisposed()) return;
      try {
        const baselineY = instance.convertToPixel({ yAxisIndex: 0 }, 0);
        const top = (typeof baselineY === 'number' ? baselineY : 0) + offsetY;
        setPositions(
          items
            .map((item) => ({
              ...item,
              left: instance.convertToPixel({ xAxisIndex: 0 }, item.name) as number,
              top,
            }))
            .filter((item) => Number.isFinite(item.left))
        );
      } catch {
        setPositions([]);
      }
    }, 60);
    return () => clearTimeout(timer);
  }, [chartRef, items, width, offsetY]);

  return (
    <>
      {positions.map((item) => (
        <span
          key={item.name}
          className="brand-axis-avatars__item"
          style={{ left: `${item.left}px`, top: `${item.top}px` }}
        >
          <BrandIcon name={item.name} color={item.color} size={size} />
        </span>
      ))}
    </>
  );
};

export default BrandAxisAvatars;
